/**
 * Quién responde por un paciente menor de edad.
 *
 * CÓMO ESTÁ PENSADO
 *
 * Se busca primero por DNI y se escribe después. En un policlínico la madre ya
 * suele estar en el sistema —trajo al hermano el mes pasado, o se atendió
 * ella— y volver a teclear su nombre es la forma más rápida de crear un
 * duplicado. Al encontrarla se dice en voz alta de quién es apoderada, para
 * que quien registra sepa que es la misma señora y no una homónima.
 *
 * El teléfono es del apoderado y no del niño: un bebé no tiene teléfono. Ese
 * número ES el contacto del paciente, y por eso se pide aquí y no en la ficha
 * del menor.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Search, Trash2, UserPlus } from "lucide-react";
import {
  type ApoderadoIn,
  type Parentesco,
  type PersonaEncontrada,
  PARENTESCOS,
  buscarPersona,
} from "../../api/pacientes";
import { TelefonoInput } from "../TelefonoInput";
import { Alert, Badge, Button, Field, Input } from "../ui";

export interface LineaApoderado extends ApoderadoIn {
  /** Solo para la interfaz: cómo se muestra sin volver a consultar. */
  _nombre?: string;
  _yaExistia?: boolean;
  _aCargoDe?: string[];
}

export function ApoderadosForm({
  valor,
  onChange,
  obligatorio,
}: {
  valor: LineaApoderado[];
  onChange: (v: LineaApoderado[]) => void;
  obligatorio: boolean;
}) {
  const [agregando, setAgregando] = useState(valor.length === 0);

  function agregar(linea: LineaApoderado) {
    // El primero es el principal: en la práctica, quien trae al niño.
    onChange([...valor, { ...linea, es_principal: valor.length === 0 }]);
    setAgregando(false);
  }

  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            Apoderado {obligatorio && <span className="text-red-600">*</span>}
          </h3>
          <p className="text-xs text-slate-600">
            {obligatorio
              ? "El paciente es menor de edad: sus datos de contacto son los de su apoderado."
              : "Opcional. Quién más puede responder por este paciente."}
          </p>
        </div>
        {!agregando && (
          <Button variant="ghost" onClick={() => setAgregando(true)}>
            <UserPlus className="h-4 w-4" />
            Agregar otro
          </Button>
        )}
      </div>

      {valor.length > 0 && (
        <ul className="space-y-2">
          {valor.map((a, idx) => (
            <li
              key={`${a.num_documento ?? a.persona_id}-${idx}`}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <div className="min-w-0 grow">
                <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-slate-900">
                  {a._nombre ?? `${a.nombres ?? ""} ${a.ap_paterno ?? ""}`.trim()}
                  {a.es_principal && <Badge variant="success">Contacto principal</Badge>}
                  {a._yaExistia && <Badge variant="neutral">Ya registrada</Badge>}
                </p>
                <p className="font-mono text-xs text-slate-500">
                  {a.tipo_documento} {a.num_documento} · {a.telefono ?? "sin teléfono"}
                </p>
                {a._aCargoDe && a._aCargoDe.length > 0 && (
                  <p className="text-xs text-slate-500">
                    También apoderada de: {a._aCargoDe.join(", ")}
                  </p>
                )}
              </div>
              <select
                value={a.parentesco}
                onChange={(e) =>
                  onChange(
                    valor.map((x, i) =>
                      i === idx ? { ...x, parentesco: e.target.value as Parentesco } : x,
                    ),
                  )
                }
                className="rounded-lg border border-slate-300 px-2 py-1 text-sm"
              >
                {PARENTESCOS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => {
                  const resto = valor.filter((_, i) => i !== idx);
                  // Si se quita al principal, el siguiente pasa a serlo: que no
                  // quede nadie marcado es peor que elegir por él.
                  if (a.es_principal && resto.length > 0) resto[0].es_principal = true;
                  onChange(resto);
                }}
                className="text-slate-400 hover:text-red-600"
                aria-label="Quitar apoderado"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {agregando && (
        <BuscarOCrearApoderado
          onListo={agregar}
          onCancelar={valor.length > 0 ? () => setAgregando(false) : undefined}
          documentosUsados={valor.map((v) => v.num_documento ?? "")}
        />
      )}

      {obligatorio && valor.length === 0 && !agregando && (
        <Alert variant="error">
          Falta el apoderado. Sin él no se puede avisar de un resultado ni pedir
          autorización para nada.
        </Alert>
      )}
    </div>
  );
}

function BuscarOCrearApoderado({
  onListo,
  onCancelar,
  documentosUsados,
}: {
  onListo: (a: LineaApoderado) => void;
  onCancelar?: () => void;
  documentosUsados: string[];
}) {
  const [doc, setDoc] = useState("");
  const [buscado, setBuscado] = useState<string | null>(null);
  const [form, setForm] = useState<LineaApoderado>({
    tipo_documento: "DNI",
    num_documento: "",
    nombres: "",
    ap_paterno: "",
    ap_materno: "",
    telefono: "",
    email: "",
    parentesco: "MADRE",
    puede_autorizar: true,
  });

  const { data: encontrada, isFetching } = useQuery({
    queryKey: ["persona", "buscar", buscado],
    queryFn: () => buscarPersona("DNI", buscado as string),
    enabled: !!buscado,
  });

  // Vuelca lo encontrado en el formulario una sola vez.
  const [volcado, setVolcado] = useState<number | null>(null);
  if (encontrada && volcado !== encontrada.persona_id) {
    setVolcado(encontrada.persona_id);
    setForm((f) => ({
      ...f,
      persona_id: encontrada.persona_id,
      num_documento: encontrada.num_documento,
      nombres: encontrada.nombres,
      ap_paterno: encontrada.ap_paterno,
      ap_materno: encontrada.ap_materno ?? "",
      telefono: encontrada.telefono ?? f.telefono,
      email: encontrada.email ?? f.email,
    }));
  }

  const yaEsta = documentosUsados.includes(doc.trim());
  const listo =
    (form.num_documento ?? "").length >= 8 &&
    (form.nombres ?? "").trim().length > 0 &&
    (form.ap_paterno ?? "").trim().length > 0;

  function confirmar(persona?: PersonaEncontrada | null) {
    onListo({
      ...form,
      num_documento: (form.num_documento ?? "").trim(),
      _nombre: persona?.nombre_completo,
      _yaExistia: !!persona,
      _aCargoDe: persona?.menores_a_cargo.map((m) => m.nombre_completo) ?? [],
    });
  }

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-3">
      <Field
        label="DNI del apoderado"
        htmlFor="ap_doc"
        hint="Se busca primero: si ya está en el sistema, no se vuelve a escribir."
      >
        <div className="flex gap-2">
          <Input
            id="ap_doc"
            value={doc}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "").slice(0, 8);
              setDoc(v);
              setForm({ ...form, num_documento: v, persona_id: null });
              setVolcado(null);
            }}
            inputMode="numeric"
            className="font-mono"
            placeholder="44490048"
          />
          <Button
            variant="ghost"
            onClick={() => setBuscado(doc)}
            disabled={doc.length !== 8 || isFetching}
          >
            <Search className="h-4 w-4" />
            Buscar
          </Button>
        </div>
      </Field>

      {yaEsta && <Alert>Ese apoderado ya está en la lista.</Alert>}

      {buscado && !isFetching && !encontrada && (
        <p className="text-xs text-slate-500">
          No está registrado. Completa sus datos y se creará junto al paciente.
        </p>
      )}

      {encontrada && (
        <Alert variant="success">
          <strong>{encontrada.nombre_completo}</strong> ya está en el sistema
          {encontrada.paciente_id ? " y es paciente del policlínico" : ""}.
          {encontrada.menores_a_cargo.length > 0 && (
            <>
              {" "}
              Es apoderada de{" "}
              {encontrada.menores_a_cargo.map((m) => m.nombre_completo).join(", ")}.
            </>
          )}
        </Alert>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Nombres" htmlFor="ap_nom">
          <Input
            id="ap_nom"
            value={form.nombres ?? ""}
            onChange={(e) => setForm({ ...form, nombres: e.target.value })}
          />
        </Field>
        <Field label="Apellido paterno" htmlFor="ap_pat">
          <Input
            id="ap_pat"
            value={form.ap_paterno ?? ""}
            onChange={(e) => setForm({ ...form, ap_paterno: e.target.value })}
          />
        </Field>
        <Field label="Apellido materno" htmlFor="ap_mat">
          <Input
            id="ap_mat"
            value={form.ap_materno ?? ""}
            onChange={(e) => setForm({ ...form, ap_materno: e.target.value })}
          />
        </Field>
      </div>

      {/* El teléfono ocupa el doble: es EL dato de contacto del paciente y
          apretujarlo entre dos campos lo vuelve ilegible justo cuando se está
          dictando un número por teléfono. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Parentesco" htmlFor="ap_par">
          <select
            id="ap_par"
            value={form.parentesco}
            onChange={(e) =>
              setForm({ ...form, parentesco: e.target.value as Parentesco })
            }
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          >
            {PARENTESCOS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field
            label="Teléfono"
            htmlFor="ap_tel"
            hint="Es el contacto del paciente."
          >
            <TelefonoInput
              id="ap_tel"
              value={form.telefono}
              onChange={(v) => setForm({ ...form, telefono: v })}
            />
          </Field>
        </div>
      </div>

      <Field label="Email" htmlFor="ap_mail" hint="Opcional.">
        <Input
          id="ap_mail"
          type="email"
          value={form.email ?? ""}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="madre@correo.pe"
        />
      </Field>

      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input
          type="checkbox"
          checked={form.puede_autorizar ?? true}
          onChange={(e) => setForm({ ...form, puede_autorizar: e.target.checked })}
        />
        Puede firmar consentimientos y autorizar procedimientos
      </label>

      <div className="flex justify-end gap-2">
        {onCancelar && (
          <Button variant="ghost" onClick={onCancelar}>
            Cancelar
          </Button>
        )}
        <Button onClick={() => confirmar(encontrada)} disabled={!listo || yaEsta}>
          <Check className="h-4 w-4" />
          Añadir apoderado
        </Button>
      </div>
    </div>
  );
}
